import type { SearchResultContentSource, SearchResultContentType } from '../../types';
import type React from 'react';
import { CheckboxGroupItem, Col, Icon, Row, P } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { APP_CONSTANTS, getCurrentUser } from '~/common/explicit-exports';
import {
  learnerCanAccessLinkedInLearning,
  learnerCanAccessUdemy,
} from '~/features/learner-content-provider-development/utils';
import {
  translatedContentTypes,
  SearchResultContentTypes,
  SearchResultContentSourceTypes,
  translatedContentSourceTypes,
} from '../../constants';
import { getIconPropsSearchResult } from '../../utils';

type ContentFilterOption = {
  type: SearchResultContentType;
  subType?: SearchResultContentSource;
  displayName: string;
};

const filterOptions = ({ cmsEnabled, linkedInEnabled, udemyEnabled }): ContentFilterOption[] => {
  let typeOptions: ContentFilterOption[] = Object.values(SearchResultContentTypes)
    .filter((type) => type !== SearchResultContentTypes.CONTENT_PROVIDER_CONTENT)
    .map((type) => ({
      type,
      displayName: translatedContentTypes[type],
    }));
  let sourceOptions: ContentFilterOption[] = Object.values(SearchResultContentSourceTypes).map((subType) => ({
    type: APP_CONSTANTS.CONTENT_PROVIDER_TOP_LEVEL_CONTENT_TYPE,
    subType,
    displayName: translatedContentSourceTypes[subType],
  }));
  let typesToExclude: SearchResultContentType[] = [];
  let subTypesToExclude: SearchResultContentSource[] = [];

  if (!cmsEnabled) {
    typesToExclude = [...typesToExclude, SearchResultContentTypes.CMS_FILE, SearchResultContentTypes.CMS_LINK];
  }

  if (!linkedInEnabled) {
    subTypesToExclude = [...subTypesToExclude, SearchResultContentSourceTypes.LINKEDIN_LEARNING];
  }

  if (!udemyEnabled) {
    subTypesToExclude = [...subTypesToExclude, SearchResultContentSourceTypes.UDEMY];
  }

  typeOptions = typeOptions.filter((ctfo) => !typesToExclude.includes(ctfo.type));
  sourceOptions = sourceOptions.filter((csfo) => !subTypesToExclude.includes(csfo.subType));

  return typeOptions.concat(sourceOptions);
};

export const AdvancedSearchContentTypeOptions: React.FC = () => {
  const { colors } = useTheme();
  const { enterprise } = getCurrentUser();
  const { cmsEnabled } = enterprise;
  const linkedInEnabled = learnerCanAccessLinkedInLearning();
  const udemyEnabled = learnerCanAccessUdemy();
  const options = filterOptions({ cmsEnabled, linkedInEnabled, udemyEnabled });

  return (
    <>
      {options
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((option, index) => {
          const { type, subType, displayName } = option;
          const displayType = subType || type;
          const { name, color } = getIconPropsSearchResult({ type, subType, colors });

          return (
            <CheckboxGroupItem key={displayType} value={displayType} autoFocus={index === 0}>
              <Row align="center" gapX={2}>
                <Col xs="auto">
                  <div data-testid={`icon-${name}`}>
                    <Icon name={name} color={color} />
                  </div>
                </Col>
                <Col>
                  <P size={1}>{displayName}</P>
                </Col>
              </Row>
            </CheckboxGroupItem>
          );
        })}
    </>
  );
};
